import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { catchError, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { AccountService } from 'src/app/services/account.service';
import { OtherService } from 'src/app/services/other.service';

@Component({
  selector: 'app-specialist-verification',
  templateUrl: './specialist-verification.component.html',
  styleUrls: ['./specialist-verification.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecialistVerificationComponent implements OnInit, OnDestroy {
  @ViewChild('detailsDialogTemplate') detailsDialogTemplate!: TemplateRef<any>;
  @ViewChild('vesselListDialogTemplate')
  vesselListDialogTemplate!: TemplateRef<any>;

  isSubmitDisabled = false;
  isReadonly = false;
  editLoadingId: number | null = null;
  strMobile = '';
  strLicenseNo = '';
  tankerProfile: any = null;
  vesselList: any[] = [];
  private vesselListModalRef: NgbModalRef | null = null;
  private detailsModalRef: NgbModalRef | null = null;
  unSubscribeAll$ = new Subject<void>();

  trackerImagePreview: string | null = null;

  // --- Reactive edit form (model keys match the mapping table) ---
  formGroup: FormGroup = this._fb.group({
    strName: ['', Validators.required],
    strFamily: ['', Validators.required],
    strID: ['', [Validators.required, this.validateStrID()]],
    strState: [null], // UI-only (not sent to API)
    strCity: [null, Validators.required],
    strMobile: ['', [this.validateStrMobileOptional()]],
    strAddress: [''],
    tiFloatingType: [null, Validators.required],
    iPower: [null, Validators.required],
    strVIN: [''],
    strComment: [''],
    strLicenseNo: [''],
    iTrackingCode: [''],
    iTrackingCode2: [''],
    iTrackingCode3: [''],
    strPaymentComment: [''],
    bInstallment: [false],
    iPayment1: [''],
    iPayment2: [''],
    iPayment3: [''],
    strPaymentDate: [{ value: '', disabled: true }], // readonly
    strIMEI: [''],
    strCellNo: [''],
    strSignatureImage: [''],
    strTrackerImage: [''],
    // UI-only helpers (not sent to API)
    bCanEdit: [false],
    tiInstallationStatus: [''],
    strInstallationStatus: [''],
  });

  floatingType$: Observable<any> = this.accountService.floatingType().pipe(
    map((response: string) => JSON.parse(response)),
    catchError(() => of(false)),
  );

  states$: Observable<any> = this.accountService.GetState().pipe(
    map((response: string) => JSON.parse(response)),
    catchError(() => of(false)),
  );

  cities$: Observable<any> | null = null;

  constructor(
    private accountService: AccountService,
    private otherService: OtherService,
    private ngbModal: NgbModal,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private _fb: FormBuilder,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const mobile = this.activatedRoute.snapshot.queryParamMap.get('mobile');
    if (mobile && /^09\d{9}$/.test(mobile)) {
      this.strMobile = mobile;
      this.checkMobile();
    }
  }

  onMobileChange(value: string, licenseField?: any): void {
    if ((value ?? '').trim()) {
      this.strLicenseNo = '';
      licenseField?.control?.markAsUntouched();
      licenseField?.control?.markAsPristine();
    }
  }

  onLicenseChange(value: string, mobileField?: any): void {
    if ((value ?? '').trim()) {
      this.strMobile = '';
      mobileField?.control?.markAsUntouched();
      mobileField?.control?.markAsPristine();
    }
  }

  submit(strMobileField: any, strLicenseNoField?: any) {
    const mobile = (this.strMobile ?? '').trim();
    const licenseNo = (this.strLicenseNo ?? '').trim();
    if (this.isSubmitDisabled || (!mobile && !licenseNo)) {
      return;
    }
    if (mobile && strMobileField.invalid) {
      strMobileField.control.markAsTouched();
      return;
    }
    this.checkMobile();
  }

  checkMobile() {
    const mobile = (this.strMobile ?? '').trim();
    const licenseNo = (this.strLicenseNo ?? '').trim();
    if ((!mobile && !licenseNo) || this.isSubmitDisabled) {
      return;
    }
    // Keep UI mutually exclusive: only one value is kept, the other is cleared.
    if (mobile) {
      this.strLicenseNo = '';
    } else if (licenseNo) {
      this.strMobile = '';
    }
    this.isSubmitDisabled = true;
    this.tankerProfile = null;
    this.vesselList = [];

    // Only one of mobile / license is sent; the other is sent empty (mobile wins).
    const model = {
      strMobile: mobile ? mobile : '',
      strLicenseNo: mobile ? '' : licenseNo,
      iSystemUser: this.otherService.specialistUser.iSystemUser,
      strSession: this.otherService.specialistUser.strSession,
    };

    this.accountService
      .specialistCheckMobile(model)
      .pipe(
        catchError(() => of(false)),
        takeUntil(this.unSubscribeAll$),
      )
      .subscribe((response: string | false | any) => {
        this.isSubmitDisabled = false;
        this.cdr.markForCheck();

        if (response) {
          const parsed = JSON.parse(response as string);
          const list = (Array.isArray(parsed) ? parsed : [parsed]).filter(
            (x) => !!x,
          );

          if (!list.length) {
            this.goToRegistration();
            return;
          }

          const first = list[0];

          if (first?.ISSessionExpired == true) {
            this.handleSessionExpired();
            return;
          }

          if (
            list.length === 1 &&
            first.iResult === 0 &&
            first.iPreregistration == null
          ) {
            return this.accountService.toastSwal('error', first.strError);
          }

          this.vesselList = list;
          this.openVesselListDialog();
          this.accountService.toastSwal('success', `عملیات با موفقیت انجام شد`);
        } else {
          this.accountService.toastSwal('error', 'خطایی رخ داد');
        }
      });
  }

  goToRegistration() {
    this.ngbModal.dismissAll();
    const mobile = (this.strMobile ?? '').trim();
    const licenseNo = (this.strLicenseNo ?? '').trim();
    this.router.navigate(['/specialist-registration'], {
      queryParams: {
        mobile: mobile,
        licenseNo: mobile ? '' : licenseNo,
        returnMobile: mobile,
      },
    });
  }

  addNewVessel() {
    this.goToRegistration();
  }

  openVesselListDialog(): void {
    if (this.vesselListModalRef) {
      return;
    }
    this.vesselListModalRef = this.ngbModal.open(
      this.vesselListDialogTemplate,
      {
        scrollable: true,
        container: 'app-specialist-verification',
        modalDialogClass: 'details-dialog',
        centered: true,
        animation: false,
      },
    );
    this.vesselListModalRef.dismissed.subscribe(
      () => (this.vesselListModalRef = null),
    );
    this.vesselListModalRef.closed.subscribe(
      () => (this.vesselListModalRef = null),
    );
  }

  editVessel(vessel: any) {
    if (!vessel?.iPreregistration || this.editLoadingId != null) {
      return;
    }
    this.editLoadingId = vessel.iPreregistration;
    this.cdr.markForCheck();

    const model = {
      iSystemUser: this.otherService.specialistUser.iSystemUser,
      strSession: this.otherService.specialistUser.strSession,
      iPreregistration: vessel.iPreregistration,
    };

    this.accountService
      .userInfo4Admin(model)
      .pipe(
        catchError(() => of(false)),
        takeUntil(this.unSubscribeAll$),
      )
      .subscribe((response: string | false | any) => {
        this.editLoadingId = null;
        this.cdr.markForCheck();

        if (response) {
          let result = JSON.parse(response as string);
          if (Array.isArray(result)) result = result[0];

          if (!result) {
            return this.accountService.toastSwal('error', 'خطایی رخ داد');
          }

          if (result?.ISSessionExpired == true) {
            this.handleSessionExpired();
            return;
          }

          if (result.iResult === 0) {
            return this.accountService.toastSwal('error', result.strError);
          }

          this.showDetails(result);
        } else {
          this.accountService.toastSwal('error', 'خطایی رخ داد');
        }
      });
  }

  showDetails(result: any) {
    this.tankerProfile = result;
    this.isReadonly = !result?.bCanEdit;
    this.trackerImagePreview = this.toDataUrl(result?.strTrackerImage) || null;

    this.formGroup.patchValue({
      strName: result?.strName ?? '',
      strFamily: result?.strFamily ?? '',
      strID: result?.strID ?? '',
      strCity: result?.strCity ?? null,
      strMobile: result?.strMobile ?? '',
      strAddress: result?.strAddress ?? '',
      tiFloatingType: result?.tiFloatingType ?? null,
      iPower: result?.iPower ?? null,
      strVIN: result?.strVIN ?? '',
      strComment: result?.strComment ?? '',
      strLicenseNo: result?.strLicenseNo ?? '',
      iTrackingCode: result?.iTrackingCode ?? '',
      iTrackingCode2: result?.iTrackingCode2 ?? '',
      iTrackingCode3: result?.iTrackingCode3 ?? '',
      strPaymentComment: result?.strPaymentComment ?? '',
      bInstallment: !!result?.bInstallment,
      iPayment1: this.toMoneyString(result?.iPayment1),
      iPayment2: this.toMoneyString(result?.iPayment2),
      iPayment3: this.toMoneyString(result?.iPayment3),
      strPaymentDate:
        result?.strPaymentDate ?? result?.strRegisterDateTime ?? '',
      strIMEI: result?.strIMEI ?? '',
      strCellNo: result?.strCellNo ?? '',
      strSignatureImage: this.toDataUrl(result?.strSignatureImage),
      strTrackerImage: this.toDataUrl(result?.strTrackerImage),
      bCanEdit: !!result?.bCanEdit,
      tiInstallationStatus: result?.tiInstallationStatus ?? '',
      strInstallationStatus: result?.strInstallationStatus ?? '',
    });

    // Dependent state -> city (same as SpecialistRegistrationComponent).
    // Keep the current city visible even when its state is unknown yet.
    const knownState = result?.strState ?? null;
    if (knownState) {
      this.formGroup.get('strState')?.setValue(knownState);
      this.loadCities(knownState);
    } else {
      const currentCity = result?.strCity ?? null;
      const currentCityName = result?.strCityName ?? result?.strCity ?? '';
      this.formGroup.get('strState')?.setValue(null);
      this.cities$ = of(
        currentCity
          ? [{ strCity: currentCity, strCityName: currentCityName }]
          : [],
      );
    }

    if (!result?.bCanEdit) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
      // strPaymentDate always stays readonly.
      this.formGroup.get('strPaymentDate')?.disable();
    }

    this.openDetailsDialog();
    this.cdr.markForCheck();
  }

  openDetailsDialog(): void {
    this.detailsModalRef = this.ngbModal.open(this.detailsDialogTemplate, {
      scrollable: true,
      container: 'app-specialist-verification',
      modalDialogClass: 'details-dialog',
      centered: true,
      animation: false,
    });
    this.detailsModalRef.dismissed.subscribe(
      () => (this.detailsModalRef = null),
    );
    this.detailsModalRef.closed.subscribe(() => (this.detailsModalRef = null));
  }

  // --- Dependent selects (identical to SpecialistRegistrationComponent) ---
  onStateChange(stateId: string | null) {
    this.formGroup.get('strCity')?.setValue(null);
    this.loadCities(stateId);
  }

  loadCities(stateId: string | null) {
    if (!stateId) {
      this.cities$ = null;
      this.cdr.markForCheck();
      return;
    }
    this.cities$ = this.accountService.GetCity(stateId).pipe(
      map((response: string) => JSON.parse(response)),
      catchError(() => of(false)),
    );
    this.cdr.markForCheck();
  }

  // --- Signature (app-signature-field -> base64) ---
  onSignatureChanged(base64: string) {
    this.formGroup.get('strSignatureImage')?.setValue(base64);
    this.formGroup.get('strSignatureImage')?.markAsDirty();
    this.cdr.markForCheck();
  }

  clearTrackerImage() {
    this.trackerImagePreview = null;
    this.formGroup.get('strTrackerImage')?.setValue('');
    this.formGroup.get('strTrackerImage')?.markAsDirty();
    this.cdr.markForCheck();
  }

  reloadVesselList() {
    const mobile = (this.strMobile ?? '').trim();
    const licenseNo = (this.strLicenseNo ?? '').trim();
    if (!mobile && !licenseNo) {
      return;
    }
    // Only one of mobile / license is sent; the other is sent empty (mobile wins).
    const model = {
      strMobile: mobile ? mobile : '',
      strLicenseNo: mobile ? '' : licenseNo,
      iSystemUser: this.otherService.specialistUser.iSystemUser,
      strSession: this.otherService.specialistUser.strSession,
    };

    this.accountService
      .specialistCheckMobile(model)
      .pipe(
        catchError(() => of(false)),
        takeUntil(this.unSubscribeAll$),
      )
      .subscribe((response: string | false | any) => {
        if (response) {
          const parsed = JSON.parse(response as string);
          const list = (Array.isArray(parsed) ? parsed : [parsed]).filter(
            (x) => !!x,
          );
          if (
            list.length &&
            !(
              list.length === 1 &&
              list[0].iResult === 0 &&
              list[0].iPreregistration == null
            ) &&
            !list[0]?.ISSessionExpired
          ) {
            this.vesselList = list;
            this.cdr.markForCheck();
          }
        }
      });
  }

    submitForm() {
    if (!this.formGroup.get('bCanEdit')?.value) {
      return;
    }

    if (this.formGroup.invalid) {
      this.markAllAsTouched(this.formGroup);
      return this.accountService.toastSwal(
        'error',
        'موارد الزامی را کامل کنید!',
      );
    }

    // Per-installment rule: tracking code without amount is not allowed.
    const pairs: Array<[string, string, string]> = [
      ['iTrackingCode', 'iPayment1', 'قسط اول'],
      ['iTrackingCode2', 'iPayment2', 'قسط دوم'],
      ['iTrackingCode3', 'iPayment3', 'قسط سوم'],
    ];
    for (const [trackKey, payKey, label] of pairs) {
      const track = (this.formGroup.get(trackKey)?.value ?? '')
        .toString()
        .trim();
      const pay = (this.formGroup.get(payKey)?.value ?? '').toString().trim();
      if (track && Number(track) > 0 && !pay) {
        this.markAllAsTouched(this.formGroup);
        return this.accountService.toastSwal(
          'error',
          `مبلغ ${label} را وارد کنید!`,
        );
      }
    }

    if (
      this.formGroup.get('bInstallment')?.value &&
      !(this.formGroup.get('strPaymentComment')?.value ?? '').trim()
    ) {
      this.markAllAsTouched(this.formGroup);
      return this.accountService.toastSwal(
        'error',
        'توضیحات شرایط پرداخت را وارد کنید!',
      );
    }

    const raw = this.formGroup.getRawValue();
    // Build the payload strictly from the mapping table (UI-only keys excluded).
    const model = this.otherService.stringNumberToNumber({
      iSystemUser: this.otherService.specialistUser.iSystemUser,
      strSession: this.otherService.specialistUser.strSession,
      iPreregistration: this.tankerProfile?.iPreregistration,
      strName: raw.strName,
      strFamily: raw.strFamily,
      strID: raw.strID,
      strCity: raw.strCity,
      strMobile: raw.strMobile,
      strAddress: raw.strAddress,
      tiFloatingType: raw.tiFloatingType,
      iPower: raw.iPower,
      strVIN: raw.strVIN,
      strComment: raw.strComment,
      strLicenseNo: raw.strLicenseNo,
      iTrackingCode: raw.iTrackingCode,
      iTrackingCode2: raw.iTrackingCode2,
      iTrackingCode3: raw.iTrackingCode3,
      strPaymentComment: raw.strPaymentComment,
      bInstallment: raw.bInstallment,
      iPayment1: raw.iPayment1,
      iPayment2: raw.iPayment2,
      iPayment3: raw.iPayment3,
      strPaymentDate: raw.strPaymentDate,
      strIMEI: raw.strIMEI,
      strCellNo: raw.strCellNo,
      strSignatureImage: this.stripDataUrlPrefix(raw.strSignatureImage),
      strTrackerImage: this.stripDataUrlPrefix(raw.strTrackerImage),
    });

    // Installment amounts default to 0 when left empty.
    for (const key of ['iPayment1', 'iPayment2', 'iPayment3'] as const) {
      const n = parseFloat(String(model[key] ?? '').replace(/,/g, ''));
      model[key] = isNaN(n) ? 0 : n;
    }

    this.accountService
      .preregistrationSetCode2(model)
      .pipe(
        catchError(() => of(false)),
        takeUntil(this.unSubscribeAll$),
      )
      .subscribe((response: string | false | any) => {
        if (response) {
          let result = JSON.parse(response);
          if (Array.isArray(result)) result = result[0];

          if (result?.ISSessionExpired == true) {
            this.handleSessionExpired();
            return;
          }

          if (result.iResult === 0) {
            return this.accountService.toastSwal('error', result.strError);
          }

          this.accountService.toastSwal('success', result.strError);
          this.detailsModalRef?.close();
          this.detailsModalRef = null;
          this.reloadVesselList();
        } else {
          this.accountService.toastSwal('error', 'خطایی رخ داد');
        }
      });
  }

  invalid(key: string): boolean {
    const c = this.formGroup.get(key);
    return !!c && c.invalid && c.touched;
  }

  validateStrMobileOptional(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = (control.value ?? '').toString().trim();
      if (!controlValue) {
        return null;
      }
      const isValid = /^09/.test(controlValue ?? '');

      return isValid ? null : { mobilePhoneNumberWrong: true };
    };
  }

  validateStrID(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value;
      const isValid = this.isValidIranianNationalCode(controlValue);
      return isValid ? null : { nationalCodeWrong: true };
    };
  }

  private isValidIranianNationalCode(code: string): boolean {
    if (!/^\d{10}$/.test(code ?? '')) {
      return false;
    }

    if (/^(\d)\1{9}$/.test(code)) {
      return false;
    }

    const digits = code.split('').map(Number);
    const check = digits[9];
    const sum = digits
      .slice(0, 9)
      .reduce((acc, digit, i) => acc + digit * (10 - i), 0);
    const remainder = sum % 11;

    return remainder < 2 ? check === remainder : check === 11 - remainder;
  }

  private toDataUrl(value: any): string {
    const v = (value ?? '').toString().trim();
    if (!v) {
      return '';
    }
    return v.startsWith('data:') ? v : `data:image/png;base64,${v}`;
  }

  private stripDataUrlPrefix(value: any): string {
    const v = (value ?? '').toString().trim();
    if (!v) {
      return '';
    }
    if (v.startsWith('data:')) {
      const idx = v.indexOf(',');
      return idx > -1 ? v.slice(idx + 1) : v;
    }
    return v;
  }

  private toMoneyString(value: any): string {
    if (value == null || value === '') {
      return '';
    }
    const n = parseFloat(String(value).replace(/,/g, ''));
    return isNaN(n) || n <= 0 ? '' : n.toLocaleString('en-US');
  }

  private handleSessionExpired() {
    this.otherService.logout();
    this.router.navigate(['/'], { queryParams: { error: 'SessionExpired' } });
    this.accountService.toastSwal(
      'error',
      'نشست کاربری شما منقضی شده است، مجددا وارد سامانه شوید',
    );
  }

  markAllAsTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.markAllAsTouched(control);
      }
    });
    this.cdr.markForCheck();
  }

  markAllAsUnTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsUntouched({ onlySelf: false });
      } else if (control instanceof FormGroup) {
        this.markAllAsUnTouched(control);
      }
    });
  }

  unSubscribeAll(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
  }

  ngOnDestroy(): void {
    this.unSubscribeAll();
  }

  private readonly MAX_IMAGE_SIZE = 10 * 1024 * 1024; // ۱۰ مگابایت
  private readonly MAX_IMAGE_WIDTH = 800;
  private readonly MAX_IMAGE_HEIGHT = 600;

  onTrackerImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.accountService.toastSwal('error', 'فقط فایل تصویری انتخاب کنید!');
      input.value = '';
      return;
    }
    if (file.size > this.MAX_IMAGE_SIZE) {
      this.accountService.toastSwal(
        'error',
        'حجم تصویر نباید بیشتر از ۱۰ مگابایت باشد!',
      );
      input.value = '';
      return;
    }

    this.resizeImageIfNeeded(file).then((base64) => {
        this.trackerImagePreview = base64;
        this.formGroup.get('strTrackerImage')?.setValue(base64);
        this.formGroup.get('strTrackerImage')?.markAsDirty();
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.accountService.toastSwal('error', 'خطا در پردازش تصویر!');
      });

    input.value = '';
  }

  private resizeImageIfNeeded(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        const scale = Math.min(1,this.MAX_IMAGE_WIDTH / img.width, this.MAX_IMAGE_HEIGHT / img.height);

        if (scale === 1) {
          this.readFileAsDataUrl(file).then(resolve, reject);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        if (outputType === 'image/jpeg') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL(outputType, 0.85));
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Image load failed'));
      };

      img.src = objectUrl;
    });
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

}
